import { IDonorProfileHlaInfoData, IDonorProfileHlaInfoLookupData } from '@nkr/models/donor-profile';
import { Col, FormControl, Panel, Row } from 'react-bootstrap';
import { HlaAntigenSelectionForm, HlaDrb345ToggleForm } from '@nkr/components/form/hla';
import * as _ from 'lodash';
import { updateProfileHlaInfo } from './update-profile-hla-info';
import { HlaLocusGroup } from '@nkr/models/hla/hla-locus-group';

interface IProps {
    data: IDonorProfileHlaInfoData,
    lookupData: IDonorProfileHlaInfoLookupData,
    onDataChange: (data: IDonorProfileHlaInfoData) => void
}

const HLAClass2 = (props: IProps) => {

    const drAntigens = props.lookupData.hlaValueList.find(x => x.hla == 'DR');
    const dqAntigens = props.lookupData.hlaValueList.find(x => x.hla == 'DQ');
    const dqaAntigens = props.lookupData.hlaValueList.find(x => x.hla == 'DQa');
    const dpAntigens = props.lookupData.hlaValueList.find(x => x.hla == 'DP');
    const dpaAntigens = props.lookupData.hlaValueList.find(x => x.hla == 'DPa');
    const alleles = props.data.alleles ?? [];

    const handleNumberChange = (e: React.ChangeEvent<HTMLSelectElement & HTMLInputElement>) => {
        let name = e.currentTarget.name;
        let value = parseInt(e.currentTarget.value);
        let data = _.cloneDeep(props.data);

        data = updateProfileHlaInfo.onNumericChange(name, value, data);

        props.onDataChange(data);
    }

    const handleStringChange = (e: React.ChangeEvent<HTMLSelectElement & HTMLInputElement>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;
        let data = _.cloneDeep(props.data);

        data = updateProfileHlaInfo.onTextChange(name, value, data);

        props.onDataChange(data);
    }

    const handleBooleanChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;
        let newValue: boolean | null;
        let data = _.cloneDeep(props.data);

        if (value == 'null') {
            newValue = null
        } else {
            newValue = value === 'true';
        }

        data = updateProfileHlaInfo.onBooleanChange(name, newValue, data);

        props.onDataChange(data);
    }

    return (
        <>
            <Col xs={12} lg={6} style={{ display: 'flex', flexDirection: 'column' }}>
                <Panel bsStyle='primary' style={{ borderColor: '#e7f1fa', flex: 1 }}>
                    <Panel.Heading style={{
                        backgroundColor: '#1e3a5f',
                        borderColor: '#1e3a5f',
                        color: '#fff',
                        textTransform: 'uppercase',
                        textAlign: 'center',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        letterSpacing: '1px'
                    }}>
                        HLA Class II
                    </Panel.Heading>
                    <Panel.Body style={{ padding: '6px' }}>

                <HlaAntigenSelectionForm
                    antigen1Name={'hlaDrb1'}
                    antigen1Value={props.data.hlaDrb1}
                    antigen2Name={'hlaDrb2'}
                    antigen2Value={props.data.hlaDrb2}
                    alleles={alleles.filter(x => x.locusGroup == HlaLocusGroup.Drb1)}
                    antigenOptions={drAntigens.antigens}
                    title={'DRB1'}
                    antigenLabel={true}
                    equivalentSignedOff={props.data.hlaDiscrepancyIsEquivalent}
                    handleAntigenChange={handleNumberChange}
                />
                
                <HlaDrb345ToggleForm
                    antigenName={'DR52'}
                    dropdownValue={props.data.hlaHasDr52}
                    dropdownName={'hlaDr52'}
                    alleles={alleles.filter(a => a.alleleDetail.startsWith('DRB3'))}
                    hasAlleleData={props.data.hasAlleleData}
                    title={'DRB3'}
                    equivalentSignedOff={props.data.hlaDiscrepancyIsEquivalent}
                    handleBooleanChange={handleBooleanChange}
                />

                <HlaDrb345ToggleForm
                    antigenName={'DR53'}
                    dropdownValue={props.data.hlaHasDr53}
                    dropdownName={'hlaDr53'}
                    alleles={alleles.filter(a => a.alleleDetail.startsWith('DRB4'))}
                    hasAlleleData={props.data.hasAlleleData}
                    title={'DRB4'}
                    equivalentSignedOff={props.data.hlaDiscrepancyIsEquivalent}
                    handleBooleanChange={handleBooleanChange}
                />

                <HlaDrb345ToggleForm
                    antigenName={'DR51'}
                    dropdownValue={props.data.hlaHasDr51}
                    dropdownName={'hlaDr51'}
                    alleles={alleles.filter(a => a.alleleDetail.startsWith('DRB5'))}
                    hasAlleleData={props.data.hasAlleleData}
                    title={'DRB5'}
                    equivalentSignedOff={props.data.hlaDiscrepancyIsEquivalent}
                    handleBooleanChange={handleBooleanChange}
                />

                <HlaAntigenSelectionForm
                    antigen1Name={'hlaDqa1'}
                    antigen1Value={props.data.hlaDqa1}
                    antigen2Name={'hlaDqa2'}
                    antigen2Value={props.data.hlaDqa2}
                    alleles={alleles.filter(x => x.locusGroup == HlaLocusGroup.Dqa1)}
                    antigenOptions={dqaAntigens.antigens}
                    title={'DQA'}
                    antigenLabel={true}
                    equivalentSignedOff={props.data.hlaDiscrepancyIsEquivalent}
                    handleAntigenChange={handleNumberChange}
                />

                <HlaAntigenSelectionForm
                    antigen1Name={'hlaDqb1'}
                    antigen1Value={props.data.hlaDqb1}
                    antigen2Name={'hlaDqb2'}
                    antigen2Value={props.data.hlaDqb2}
                    alleles={alleles.filter(x => x.locusGroup == HlaLocusGroup.Dqb1)}
                    antigenOptions={dqAntigens.antigens}
                    title={'DQB'}
                    antigenLabel={true}
                    equivalentSignedOff={props.data.hlaDiscrepancyIsEquivalent}
                    handleAntigenChange={handleNumberChange}
                />

                <HlaAntigenSelectionForm
                    antigen1Name={'hlaDpa1'}
                    antigen1Value={props.data.hlaDpa1}
                    antigen2Name={'hlaDpa2'}
                    antigen2Value={props.data.hlaDpa2}
                    alleles={alleles.filter(x => x.locusGroup == HlaLocusGroup.Dpa1)}
                    antigenOptions={dpaAntigens.antigens}
                    title={'DPA'}
                    antigenLabel={true}
                    equivalentSignedOff={props.data.hlaDiscrepancyIsEquivalent}
                    handleAntigenChange={handleNumberChange}
                />

                <HlaAntigenSelectionForm
                    antigen1Name={'hlaDpb1'}
                    antigen1Value={props.data.hlaDpb1}
                    antigen2Name={'hlaDpb2'}
                    antigen2Value={props.data.hlaDpb2}
                    alleles={alleles.filter(x => x.locusGroup == HlaLocusGroup.Dpb1)}
                    antigenOptions={dpAntigens.antigens}
                    title={'DPB'}
                    antigenLabel={true}
                    equivalentSignedOff={props.data.hlaDiscrepancyIsEquivalent}
                    handleAntigenChange={handleNumberChange}
                />
                    </Panel.Body>
                </Panel>
            </Col>
        </>
    )
}

export default HLAClass2;